// Segment analytics loader + thin wrapper.
//
// Gated on the REACT_APP_SEGMENT_WRITE_KEY build-time env var. It is currently
// set only in each dev's git-ignored .env.local (local dev), NOT in the DEV
// deploy pipeline — so the deployed DEV site does not load Segment today. To
// enable analytics on deployed DEV, add REACT_APP_SEGMENT_WRITE_KEY to
// deploy-dev.yml from a repo secret (mirroring REACT_APP_BUGHERD_KEY).
// Production builds never set it, so nothing loads there. There is no fallback
// key on purpose: no key means no analytics. Mirrors shared/bugherd.js.
//
// Usage:
//   import { trackEvent } from "shared/analytics";
//   // Every event automatically carries the shared context (app, env, admin user).
//   // Attach IBO info per event where the acted-on entity has it, e.g. an order:
//   trackEvent("Order Status Changed", {
//     orderId,
//     status,
//     iboNumber,   // IBO the order belongs to
//     iboPinLevel,
//   });

let loaded = false;

// Super-properties merged into every event (set once auth resolves).
let context = {};

function segment() {
  return typeof window !== "undefined" ? window.analytics : undefined;
}

/**
 * Load the Segment analytics.js bundle. No-op without a write key, if already
 * loaded, or outside the browser.
 */
export function initAnalytics() {
  if (loaded) return;
  if (typeof document === "undefined") return;

  const writeKey = process.env.REACT_APP_SEGMENT_WRITE_KEY;
  if (!writeKey) return;

  loaded = true;

  // Standard Segment analytics.js stub: queues calls until the CDN bundle
  // finishes loading, then replays them.
  const analytics = (window.analytics = window.analytics || []);
  if (analytics.initialize || analytics.invoked) return;
  analytics.invoked = true;
  analytics.methods = [
    "trackSubmit",
    "trackClick",
    "trackLink",
    "trackForm",
    "pageview",
    "identify",
    "reset",
    "group",
    "track",
    "ready",
    "alias",
    "debug",
    "page",
    "once",
    "off",
    "on",
    "addSourceMiddleware",
    "addIntegrationMiddleware",
    "setAnonymousId",
    "addDestinationMiddleware",
  ];
  analytics.factory = function (method) {
    return function (...args) {
      args.unshift(method);
      analytics.push(args);
      return analytics;
    };
  };
  for (const method of analytics.methods) {
    analytics[method] = analytics.factory(method);
  }
  analytics.load = function (key, options) {
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    script.src = `https://cdn.segment.com/analytics.js/v1/${encodeURIComponent(
      key
    )}/analytics.min.js`;
    const first = document.getElementsByTagName("script")[0];
    first.parentNode.insertBefore(script, first);
    analytics._loadOptions = options;
  };
  analytics._writeKey = writeKey;
  analytics.SNIPPET_VERSION = "4.15.3";
  analytics.load(writeKey);
  analytics.page();
}

/**
 * Merge properties into the shared context attached to every subsequent event.
 * Call once the admin identity is known (see App.js).
 */
export function setAnalyticsContext(next) {
  context = { ...context, ...(next || {}) };
}

/**
 * Associate the current admin (support-staff) user with the analytics session.
 */
export function identifyUser(userId, traits = {}) {
  const a = segment();
  if (!a) return;
  const merged = { ...context, ...traits };
  if (userId) {
    a.identify(userId, merged);
  } else {
    a.identify(merged);
  }
}

/**
 * Track a named event. Shared context is merged in automatically; pass any
 * event-specific properties (including IBO info) in `properties`.
 */
export function trackEvent(event, properties = {}) {
  const a = segment();
  if (!a || !event) return;
  a.track(event, { ...context, ...properties });
}

/**
 * Track a page view. Route changes can call this to record admin navigation.
 */
export function trackPage(name, properties = {}) {
  const a = segment();
  if (!a) return;
  if (name) {
    a.page(name, { ...context, ...properties });
  } else {
    a.page({ ...context, ...properties });
  }
}
